import { ChakraProvider } from '@chakra-ui/react';
import montserratWoff2Cyrillic from '@fontsource-variable/montserrat/files/montserrat-cyrillic-wght-normal.woff2?url';
import montserratWoff2 from '@fontsource-variable/montserrat/files/montserrat-latin-wght-normal.woff2?url';
import { I18n } from '@lingui/core';
import { wrapCreateRootRouteWithSentry } from '@sentry/tanstackstart-react';
import { domAnimation, LazyMotion } from 'motion/react';
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouteContext,
} from '@tanstack/react-router';

import { AuthStatus, safeCheckIsAuth } from '#modules/user/features/auth/utils/checkIsAuth';
import { gtmScripts } from '#features/analytics/components/GtmScript';
import { useNativeAuthRehydrate } from '#features/api/utils/useNativeAuthRehydrate';
import { useNativeOAuthDeepLink } from '#features/api/utils/useNativeOAuthDeepLink';
import { AppUpdateGate } from '#features/app-update/components/AppUpdateGate';
import { useNativeCallBridge } from '#features/calls/hooks/useNativeCallBridge';
import { useWebCallLink } from '#features/calls/hooks/useWebCallLink';
import { ErrorNotFound } from '#features/error/components/ErrorNotFound';
import { MainLayout } from '#features/layout/components/MainLayout';
import { useNativeLocaleSync } from '#features/i18n/hooks/useNativeLocaleSync';
import { RouterProgressBar } from '#features/layout/components/RouterProgressBar';
import { useNativeBackButton } from '#features/layout/hooks/useNativeBackButton';
import { useNativeViewportFit } from '#features/layout/hooks/useNativeViewportFit';
import { PushOnboarding } from '#features/notifications/components/PushOnboarding';
import { useNativePushTap } from '#features/notifications/utils/useNativePushTap';
import { useNativePushTokenSync } from '#features/notifications/utils/useNativePushTokenSync';
import { useWebPushTokenSync } from '#features/notifications/utils/useWebPushTokenSync';
import { useNativeAppLinks } from '#features/routing/hooks/useNativeAppLinks';
import { useCallScreenNudge } from '#features/notifications/hooks/useCallScreenNudge';
import { useStoreReviewPrompt } from '#features/store-review/hooks/useStoreReviewPrompt';
import { ColorModeProvider } from '#features/ui/components/colorMode';
import { setupImageConfig } from '#features/ui/components/Image/setupImageConfig';
import { Toaster } from '#features/ui/components/Toaster';
import globalCss from '#features/ui/globals.css?inline';
import { system } from '#features/ui/theme/customTheme';
import { envConfig } from '#constants/envConfig';
import { seo } from '#utils/seo';

const title = 'Teamplay.gg - Looking for group (LFG) community for gamers.';
const description = `Team finder for your favourite battle royal, shooter, MMO games.
Find best teammates and meet new gaming friends on PC and consoles. 
Search for squads, have fun or play competitive.`;
const image = `${envConfig.HOST}/preview.png`;

setupImageConfig();

// Keeps platform-level notification plumbing in one place: native shell events (OAuth
// deep-link callback, push taps) plus the push-token reconciliation each platform needs.
// The native hooks are gated on IS_NATIVE and tree-shake their Capacitor imports away on web;
// the web sync is gated the other way round.
const PlatformBridge = () => {
  // Отметка «дерево смонтировалось» для сторожа гидрации в client.tsx: без неё он не отличает
  // застрявшую пустую оболочку от нормально работающей страницы. Ставится один раз и навсегда.
  useEffect(() => {
    document.documentElement.dataset.hydrated = 'true';
  }, []);

  useNativeAuthRehydrate();
  useNativeOAuthDeepLink();
  useNativeAppLinks();
  useNativeBackButton();
  useNativeViewportFit();
  useNativeLocaleSync();
  useNativePushTap();
  useNativeCallBridge();
  useWebCallLink();
  useNativePushTokenSync();
  useWebPushTokenSync();
  useStoreReviewPrompt();
  // Разрешение на показ звонка поверх блокировки просим у того, кто только что звонил сам, —
  // до этого просьба была бы разговором ни о чём. Живёт здесь, потому что слушает состояние
  // звонка, а оверлей звонка монтируется и размонтируется вместе с ним.
  useCallScreenNudge();
  return (
    <>
      {/*
        Проверка версии приложения. Ничего не рендерит в вебе и до того, как ответит
        /app/status.json, поэтому старт не задерживает и веб-бандл не утяжеляет.
      */}
      <AppUpdateGate />
      <PushOnboarding />
    </>
  );
};

const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <ChakraProvider value={system}>
      <ColorModeProvider enableSystem={false} forcedTheme="dark">
        <RouterProgressBar />
        <PlatformBridge />
        {children}
        <Toaster />
      </ColorModeProvider>
    </ChakraProvider>
  );
};

const Devtools =
  process.env.NODE_ENV === 'development'
    ? (await import('#features/layout/components/Devtools')).Devtools
    : () => null;

export const Route = wrapCreateRootRouteWithSentry(
  createRootRouteWithContext<{
    queryClient: QueryClient;
    isAuth: boolean;
    i18n: I18n;
    authStatus: AuthStatus;
  }>()({
    beforeLoad: async () => {
      const { isAuth, status } = await safeCheckIsAuth();

      return { isAuth, authStatus: status };
    },
    scripts: () => {
      return [
        // Здесь был инлайн-скрипт css-preload-recovery: он ловил window-'error' с текстом
        // «Unable to preload CSS» и перезагружал страницу. Скрипт мёртв с тех пор, как client.tsx
        // начал гасить событие vite:preloadError — до window-'error' оно больше не доходит, —
        // а держал он при этом собственную, четвёртую по счёту защёлку перезагрузки. Отказ
        // стилевого префетча теперь разбирается там же, в обработчике vite:preloadError.
        //
        // Web-only analytics. The native shell ships neither: a cookie banner inside an
        // app is meaningless (and the store expects consent handled by the platform),
        // and GTM would be loading a remote script into the app origin for nothing.
        // tracking.pageView survives their absence — see the note in analytics/tracking.
        !envConfig.IN_DEV && !envConfig.IS_NATIVE
          ? {
              id: 'cookieyes',
              type: 'text/javascript',
              async: true,
              src: 'https://cdn-cookieyes.com/client_data/2aee7c5fb3768ede4e3c0cb4/script.js',
            }
          : undefined,
        ...(envConfig.IS_NATIVE
          ? []
          : gtmScripts.map((script, index) => ({
              id: `google-tag-manager-${index}`,
              type: 'text/javascript',
              async: true,
              children: script,
            }))),
      ].filter(Boolean);
    },
    head: () => ({
      meta: [
        ...seo({ title, description, image }),
        // Which build served this page. The About screen shows the same string, but only to
        // signed-in users; a bug report from a guest ("the button does nothing") otherwise
        // arrives with no way to tell which of the day's deploys they were on. Cheaper than
        // a visible version line and readable from view-source or a saved page.
        {
          name: 'build',
          content: `${import.meta.env.PACKAGE_VERSION}+${import.meta.env.BUILD_SHA}`,
        },
      ],
      styles: [{ children: globalCss }],
      links: [
        // { rel: 'stylesheet', href: globalCss },
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: montserratWoff2,
          crossOrigin: 'anonymous',
        },
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: montserratWoff2Cyrillic,
          crossOrigin: 'anonymous',
        },
        {
          rel: 'preconnect',
          crossOrigin: 'anonymous',
          href: envConfig.MEDIA_URL,
        },
        {
          rel: 'preconnect',
          crossOrigin: 'anonymous',
          href: envConfig.SENTRY_URL,
        },
      ],
    }),
    shellComponent: ({ children }) => {
      // oxlint-disable-next-line react/rules-of-hooks -- TanStack Start shellComponent is a React component
      const locale = useRouteContext({ from: '__root__', select: (state) => state.i18n.locale });

      return (
        <>
          <html lang={locale} translate="no" suppressHydrationWarning>
            <head>
              <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
              <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
              <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
              <link rel="manifest" href="/site.webmanifest" />
              <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#4647D2" />
              <meta name="viewport" content="width=device-width, initial-scale=1.0" />
              <meta name="google" content="notranslate" />
              <meta name="msapplication-TileColor" content="#4647D2" />
              <meta name="theme-color" content="#121317" />
              <HeadContent />
            </head>
            <body>
              <Providers>{children}</Providers>
              <Devtools />
              <Scripts />
            </body>
          </html>
        </>
      );
    },
    /*
      Экран «страницы нет» — тоже страница сайта: с шапкой, подвалом и переключателем языка.
      Голый `<ErrorNotFound />` выпадал из сайта совсем — человек оставался с двумя строками
      на пустом фоне и единственным выходом «На главную». Ловится именно здесь: неизвестный
      путь не доходит до `_layout` и разбирается корневым маршрутом.
    */
    notFoundComponent: () => (
      <LazyMotion features={domAnimation}>
        <MainLayout plain>
          <ErrorNotFound />
        </MainLayout>
      </LazyMotion>
    ),
  })
);
